import { useCallback, useEffect, useState } from 'react';
import Service from "./service";
import DashboardView from './view';
import { getJWKS, onTokenUpdate, writeXLSFile } from 'utils';
import InputAdornment from '@mui/material/InputAdornment';
import CheckIcon from "@mui/icons-material/Check";
import { DEFAULT_ERROR_MESSAGE, noop } from 'shared/constants';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import ErrorSymbol from 'assets/images/error.svg';


let accountId = null;
const defaultState = {
  uid: '',
  jwksToken: null,
  accountName: null,
  isUidIncorrect: false,
  isLoading: false,
  isErrorEncountered: false,
  progressErrors: [],
  syncDialog: {},
  isTemplateDownloaded: false,
  errors: {
    uid: " "
  },
  isSyncing: false,
  syncingProgress: 0,
  isSyncCompleted: false,
  syncErrors: [],
  gridData: [],
  version: {
    build: 0,
    realease: 0,
  },
  isFetchingSites: false,
  isFetchingCountries: false,
  siteList: [],
  countryList: [],
  toastDialog: {
    show: false,
    heading: <></>,
    content: <></>,
    contentActionText: '',
    customIcon: null,
    contentAction: noop,
  },
}

export default function Dashboard() {
  const [state, setState] = useState(defaultState);

  const handleKeyDown = evt => {
    const { value } = evt.target;
    if (parseInt(evt.keyCode) === 13) {
      if (
        !value ||
        accountId === value ||
        !!(state.errors.uid).trim() ||
        state.jwksToken
      ) {
        return;
      }
      fetchJwksToken(value);
      accountId = value;
    }
  };

  const handleBlur = evt => {
    const { value } = evt.currentTarget;
    if (
      !value ||
      accountId === value ||
      !!(state.errors.uid).trim() ||
      state.jwksToken
    ) {
      return;
    }
    fetchJwksToken(value);
    accountId = value;
  };

  const validate = (name, value) => {
    let errorMessage = " ";
    if (name === 'uid') {
      if (!value) {
        errorMessage = "Required";
      } else if (isNaN(Number(value))) {
        errorMessage = "Must be a valid number";
      } else if ((value || "").length > 19) {
        errorMessage = "Max: 19 char(s)";
  
      }
    }
    return errorMessage;
  };

  const handleChange = evt => {
    const { name, value } = evt.currentTarget;
    const errorMessage = validate(name, value);
   
    accountId = null;
    setState(prevState => ({
      ...defaultState,
      [name]: value,
      errors: {
        ...prevState,
        [name]: errorMessage,
      }
    }));
  };

  const fetchSiteList = async (uid, jwksToken, query = '') => {
    setState(prevState => ({ ...prevState, isFetchingSites: true }));
    const response = await Service.getSites(uid, jwksToken, query);
    let result = [];
    handleJwksResponse(response, data => (result = data || []));
    setState(prevState => ({ ...prevState, isFetchingSites: false, siteList: result }));
    return result;
  };

  const fetchCountryList = async (jwksToken, query = '') => {
    setState(prevState => ({ ...prevState, isFetchingCountries: true }));
    const response = await Service.getCountryList(jwksToken, query);
    let result = [];
    handleJwksResponse(response, data => (result = data || []));
    setState(prevState => ({ ...prevState, isFetchingCountries: false, countryList: result }));
    return result;
  };

  const fetchAccountName = async (uid = '') => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await Service.getAccountName((uid || '').trim());
    if (error) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        toastDialog: {
          show: true,
          content: `Alert: ${Array.isArray(error) ? error[0].message : error}`,
          heading: 'UID Not Found',
          contentActionText: 'Close and Continue',
          customIcon: ErrorSymbol,
          contentAction: () => {
            setState(prevState => ({
              ...prevState,
              toastDialog: defaultState.toastDialog,
            }));
          },
        },
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        accountName: data?.account?.name || defaultState.accountName,
      }));
    }
  };

  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await getJWKS(accountUid);
    if (error) {
      const isUidNotFound =
        Array.isArray(error) && error[0].errors[0].errorCode === 'OAU-222';
      const errorMessage = isUidNotFound
        ? 'Alert: UID not located in Salesforce.'
        : `Error: ${Array.isArray(error) ? error[0].errors[0].message : error}`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        toastDialog: {
          show: true,
          heading: isUidNotFound ? 'UID Not Found' : 'Report Error...',
          content: errorMessage,
          contentActionText: 'Close and Continue',
          customIcon: ErrorSymbol,
          contentAction: () => {
            setState(prevState => ({
              ...prevState,
              toastDialog: defaultState.toastDialog,
            }));
          },
        },
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
      }));
    }
  };

  const handleJwksResponse = (response = {}, callBack = noop) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0].errorCode === 'OAU-213') {
        return false;
      }
    }
    callBack(data, error, paging);
    if (token) {
      setState(prevState => ({
        ...prevState,
        jwksToken: token,
      }));
    }
  };

  const handleSync = async (locationsData = []) => {
    const locations = locationsData.map(location => {
      const matchedSite = state.siteList.find(site => (site?.name || "").toLowerCase() === (location?.site?.name || "").toLowerCase());
      const country = state.countryList.find(country => {
        const locationCountryName = (location?.address?.country || "").toLowerCase();
        const countryName = (country?.name || "").toLowerCase();
        const countryISO = (country?.isoCode || "").toLowerCase();
        return (countryName === locationCountryName) || (countryISO === locationCountryName);
      });
      return {
        ...location,
        site: {
          ...location.site,
          id: matchedSite?.id ?? "",
        },
        countryIsoCode: country?.isoCode,
      }
    });
    setState(prevState => ({
      ...prevState,
      isSyncing: true,
      syncingProgress: 0,
      isSyncCompleted: false,
      syncErrors: [],
      isErrorEncountered: false,
      gridData: locations
    }));

    for await (let [index, location] of locations.entries()) {
      let token = null;
      setState(prevState => {
        token = prevState.jwksToken;
        return {
          ...prevState,
        };
      });
      let response = {};

      if (!location.site.name || location.site.name === '') {
        location.site.name = 'Main Site'
        location.site.id = 'main-site'
      }

      if (!location?.site?.id) {
        response = { error : `Site(${location?.site?.name}) not found.`};
      } else if (!location?.countryIsoCode) {
        response = { error : `Country "${location?.address?.country}" is invalid.`};
      }

      if (location?.site?.id && location?.countryIsoCode) {
        const { countryIsoCode , ...payload } = location
        response = await Service.createEmeregencyLocations(state.uid, state.jwksToken,{
          ...payload,
          address: {
            ...payload.address,
            country: countryIsoCode
          }
        });
      }
      handleJwksResponse(response, (data, error) => {
        setState(prevState => {
          const isCompleted = index >= locations.length - 1;
          let syncErrors = [...prevState.syncErrors];
          if ((error || []).length) {
            syncErrors.push({
              message: error[0]?.message || error || 'Something went wrong, Please check your internet connection or contact your system administrator.',
              rowNumber: index + 1,
            });
          }
          return {
            ...prevState,
            syncErrors,
            syncingProgress: prevState.syncingProgress + 100 / locations.length,
            isSyncCompleted: isCompleted,
            isSyncing:
              isCompleted ? false : prevState.isSyncing,
            isErrorEncountered: !!(error || []).length
              ? true
              : prevState.isErrorEncountered,
            syncDialog: isCompleted
              ? {
              ...prevState.syncDialog,
              open: isCompleted,
              type: !!syncErrors.length ? 'error': 'success'
            } : prevState.syncDialog
            // toastDialog: isCompleted
            //   ? {
            //       show: true,
            //       content: !!prevState.errors.length
            //         ? 'Errors detected'
            //         : 'Success! Your data has been updated.',
            //       heading: 'SYNC COMPLETED',
            //       contentAction: () => {
            //         if (!!prevState.errors.length) {
            //           setState(prevState => ({
            //             ...prevState,
            //             toastDialog: defaultState.toastDialog,
            //           }));
            //         } else {
            //           window.location.reload();
            //         }
            //       },
            //     }
            //   : prevState.toastDialog,
          };
        });
      });
    }
  };

  const fetchVersion = async () => {
    const { data, error } = await Service.getVersion();
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setState(prevState => ({
        ...prevState,
        version: {
          ...prevState.version,
          build: data?.build || defaultState.build,
          release: data?.release || defaultState.release,
        }
      }));
    }
  };

  useEffect(() => {
    fetchVersion();
  }, []);

  useEffect(() => {
    if (!!state.jwksToken) {
      fetchAccountName(state.uid);
      fetchSiteList(state.uid, state.jwksToken);
      fetchCountryList(state.jwksToken);
    }
  }, [state.jwksToken]);

  return (
    <DashboardView
      jwksToken={state.jwksToken}
      uid={state.uid}
      errors={state.errors}
      isLoading={state.isLoading}
      isUidIncorrect={state.isUidIncorrect}
      accountName={state.accountName}
      syncingProgress={state.syncingProgress}
      syncErrors={state.syncErrors}
      syncDialog={state.syncDialog}
      isSyncing={state.isSyncing}
      isTemplateDownloaded={state.isTemplateDownloaded}
      isSyncCompleted={state.isSyncCompleted}
      isErrorEncountered={state.isErrorEncountered}
      status={state.status}
      toastDialog={state.toastDialog}
      handleKeyDown={handleKeyDown}
      handleBlur={handleBlur}
      handleChange={handleChange}
      handleSync={handleSync}
      gridData={state.gridData}
      version={state.version}
      handleSyncDialogClose={() => {
        setState(prevState => ({
          ...prevState,
          syncDialog: defaultState.syncDialog,
        }));
      }}
      onTemplateDownload={() => {
        setState(prevState => ({
          ...prevState,
          isTemplateDownloaded: true,
        }));
      }}
      onContinue={() => {
        setState(prevState => ({
          ...prevState,
          isSyncing: false,
          syncingProgress: 0,
          isSyncCompleted: false,
          syncErrors: [],
          gridData: [],
        }));
      }}
      onToastClose={() => {
        setState(prevState => ({
          ...prevState,
          isErrorEncountered: defaultState.isErrorEncountered,
          toastDialog: {
            ...defaultState.toastDialog,
          },
        }));
      }}
    />
  )
}
